package com.arena_web.pages.clubs

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.ClubDetailViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.data.dto.ClubDto
import com.arena_web.data.dto.ClubGalleryImageDto
import com.arena_web.data.dto.ClubPostDto
import com.arena_web.data.dto.ClubReviewDto
import com.arena_web.data.dto.ClubRuleDto
import com.arena_web.data.dto.ClubScheduleDto
import com.arena_web.data.dto.WorkingHoursDto
import com.arena_web.pages.clubDistanceKm
import com.arena_web.pages.clubSportLabel
import com.arena_web.pages.formatClubKm
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.arena_web.utils.getIpLocation
import com.arena_web.utils.getUserCoordinates
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.sections.clubdetail.mobile.ClubDetailContentMobile
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color as KobwebColor
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

private data class AmenityInfo(val type: String, val label: String, val icon: String)

private val ALL_AMENITIES = listOf(
    AmenityInfo("PARKING", "Parking", AppIcons.Parking),
    AmenityInfo("CAFE", "Cafe", AppIcons.Cafe),
    AmenityInfo("WIFI", "WiFi", AppIcons.Wifi),
    AmenityInfo("RECEPTION", "Reception", AppIcons.Reception),
    AmenityInfo("SHOWER", "Shower", AppIcons.Shower),
    AmenityInfo("FIRST_AID", "First aid", AppIcons.FirstAid),
)

@Page("/clubs/{id}")
@Composable
fun ClubDetailPage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val clubId = ctx.route.path.substringAfterLast("/")

    val viewModel = rememberViewModel(key = clubId) {
        ClubDetailViewModel(
            clubId = clubId,
            clubPublicRepository = container.clubPublicRepository,
            clubAdminRepository = container.clubAdminRepository
        )
    }

    val t = LocalAppStrings.current
    val state by viewModel.uiState.collectAsState()

    // User position for the "N km away" chip — GPS when permitted, IP-derived
    // otherwise (same fallback the Arena page uses).
    var userCoords by remember { mutableStateOf<Pair<Double, Double>?>(null) }
    LaunchedEffect(Unit) {
        userCoords = getUserCoordinates()?.let { it.latitude to it.longitude }
            ?: getIpLocation()?.let { it.lat to it.lng }
    }

    val mobile = isMobile()
    AppPageLayout(
        activeTab = SidebarTab.Arena,
        mobileReady = true,
        mobileHideBottomNav = true,
        mobileHideTopBar = true,
        mobileEdgeToEdge = mobile,
    ) {
      if (mobile) {
        // Phones replicate the KMP app's ClubDetailScreen (hero photo, info
        // card, Photos | Amenities | Map tabs).
        ClubDetailContentMobile(
            state = state,
            userCoords = userCoords,
            onBack = {
                // Land on the Clubs tab — same promise as the desktop back row.
                Storage.save(StorageKeys.ARENA_TAB, "Clubs")
                ctx.router.navigateTo(Routes.ARENA)
            },
            onRate = { stars -> viewModel.submitReview(stars, "") },
        )
      } else {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(32, 40)
                .sPaddingBottom(30)
                .sGap(24)
        ) {
            // Back button
            Row(
                modifier = Modifier
                    .sGap(8)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        // Land on the Clubs tab — the label promises it.
                        Storage.save(StorageKeys.ARENA_TAB, "Clubs")
                        ctx.router.navigateTo(Routes.ARENA)
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    src = AppIcons.ChevronForward,
                    modifier = Modifier.styleModifier {
                        property("transform", "rotate(180deg)")
                    }
                )
                SpanText(
                    t.pageBackToClubs,
                    modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }

            if (state.isLoading) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    // Intentionally empty — no loading indicator (user pref).
                }
            } else if (state.error != null) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        state.error ?: t.pageSomethingWentWrong,
                        modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            } else {
                // Figma 11475:424824 — Personal Info card on top (data left,
                // photo right), then gallery | amenities | map in one row.
                // Court Rules, the Posts Feed, the Reviews block and the Book
                // button were removed (user decision 2026-08); rating happens
                // via the stars dropdown under the photo's rating chip.
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .minHeight(0.px)
                        .sGap(20)
                        .overflow(Overflow.Auto)
                        .styleModifier {
                            property("scrollbar-width", "thin")
                            property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        }
                ) {
                    ClubPersonalInfoCard(
                        club = state.club,
                        schedule = state.schedule,
                        userCoords = userCoords,
                        reviews = state.reviews,
                        isSubmittingReview = state.isSubmittingReview,
                        onRate = { stars -> viewModel.submitReview(stars, "") }
                    )

                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sGap(20)
                            .styleModifier {
                                property("flex", "1")
                                property("min-height", "0")
                            }
                    ) {
                        ClubDetailGalleryGrid(gallery = state.gallery)
                        ClubSectionPanel(title = t.pageAmenities) {
                            ClubDetailAmenities(amenities = state.amenities)
                        }
                        ClubSectionPanel(title = t.pageMap, flexBasis = 580) {
                            ClubDetailMapSection(club = state.club)
                        }
                    }
                }
            }
        }
      }
    }
}

// ── Personal Info card (Figma 11475:431184: data left, photo right) ──

/** Whether the club is open right now; [time] is the closing time when open,
 *  today's opening time when it opens later today, null otherwise. */
private data class OpenStatusInfo(val isOpen: Boolean, val time: String?)

private fun openStatus(workingHours: List<WorkingHoursDto>): OpenStatusInfo? {
    if (workingHours.isEmpty()) return null
    val now = kotlin.js.Date()
    // JS Date.getDay(): 0 = Sunday … 6 = Saturday → ISO 1 = Monday … 7 = Sunday.
    val isoDay = ((now.getDay() + 6) % 7) + 1
    val hhmm = now.getHours().toString().padStart(2, '0') + ":" +
        now.getMinutes().toString().padStart(2, '0')
    val today = workingHours.firstOrNull { it.dayOfWeek == isoDay }
    if (today != null && !today.isClosed) {
        val open = today.openTime.take(5)
        val close = today.closeTime.take(5)
        if (hhmm >= open && hhmm < close) return OpenStatusInfo(isOpen = true, time = close)
        if (hhmm < open) return OpenStatusInfo(isOpen = false, time = open)
    }
    return OpenStatusInfo(isOpen = false, time = null)
}

private fun currentIsoDay(): Int = ((kotlin.js.Date().getDay() + 6) % 7) + 1

/** Localized short weekday name for an ISO day (1 = Mon) via Intl —
 *  avoids adding 7×8 day-name strings to the locale files. */
private fun localizedWeekdayShort(isoDay: Int): String {
    val lang = Storage.get(StorageKeys.LANGUAGE)?.lowercase() ?: "en"
    val locale = if (lang == "ua") "uk" else lang
    // 2024-01-01 was a Monday.
    val d = kotlin.js.Date(kotlin.js.Date.UTC(2024, 0, isoDay))
    return d.asDynamic().toLocaleDateString(locale, js("({ weekday: 'short', timeZone: 'UTC' })")) as String
}

/** Device-local favorite clubs (comma-separated ids in Storage) — written by
 *  the club-page heart, mobile-app parity. */
private fun favoriteClubIds(): Set<String> =
    Storage.get(StorageKeys.FAVORITE_CLUB_IDS)
        ?.split(",")?.filter { it.isNotBlank() }?.toSet().orEmpty()

private fun setFavoriteClub(id: String, favorite: Boolean) {
    val ids = favoriteClubIds().toMutableSet()
    if (favorite) ids.add(id) else ids.remove(id)
    Storage.save(StorageKeys.FAVORITE_CLUB_IDS, ids.joinToString(","))
}

@Composable
private fun ClubPersonalInfoCard(
    club: ClubDto?,
    schedule: ClubScheduleDto?,
    userCoords: Pair<Double, Double>?,
    reviews: List<ClubReviewDto>,
    isSubmittingReview: Boolean,
    onRate: (Int) -> Unit,
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val workingHours = schedule?.workingHours.orEmpty()
    val status = openStatus(workingHours)
    val distanceKm = userCoords?.let { (uLat, uLng) ->
        clubDistanceKm(uLat, uLng, club?.latitude, club?.longitude)
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(420)
            .sBorderRadius(22)
            .styleModifier {
                property("flex-shrink", "0")
                property("background", "#00143B")
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property("box-shadow", "0 ${s(4)} ${s(14)} 0 #00245C")
                property("box-sizing", "border-box")
            }
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(16, 18)
                .sGap(28)
                .styleModifier { property("box-sizing", "border-box") }
        ) {
            // Left — venue data column; sport pill pinned to the bottom right.
            Column(
                modifier = Modifier
                    .sWidth(437)
                    .fillMaxHeight()
                    .sPaddingLeft(12)
                    .sPaddingTop(12)
                    .styleModifier { property("flex-shrink", "0") },
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                Column(modifier = Modifier.sGap(40)) {
                    Column(modifier = Modifier.sGap(28)) {
                        // Name + address
                        Column(modifier = Modifier.sGap(8)) {
                            SpanText(
                                club?.name ?: "",
                                modifier = Modifier
                                    .sFontSize(32)
                                    .fontWeight(AppTypography.SemiBold)
                                    .sLineHeight(38)
                                    .sLetterSpacing(0.64)
                                    .color(AppColors.PrimaryWhite)
                            )
                            Row(
                                modifier = Modifier.sGap(4),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    src = AppIcons.Location,
                                    modifier = Modifier.sSize(23).styleModifier {
                                        property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                                        property("flex-shrink", "0")
                                    }
                                )
                                SpanText(
                                    club?.address ?: "",
                                    modifier = Modifier
                                        .sFontSize(20)
                                        .sLineHeight(26)
                                        .sLetterSpacing(0.2)
                                        .color(KobwebColor.hex("BED6F5"))
                                )
                            }
                        }
                        // Open-now (tap → weekly schedule) + distance chips.
                        if (status != null || distanceKm != null) {
                            Row(modifier = Modifier.sGap(12)) {
                                status?.let { OpenNowChip(it, workingHours) }
                                distanceKm?.let { km ->
                                    InfoPillChip(onClick = null) {
                                        Icon(
                                            src = AppIcons.Location,
                                            modifier = Modifier.sSize(23).styleModifier {
                                                property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                                            }
                                        )
                                        SpanText(
                                            t.clubKmAway(formatClubKm(km)),
                                            modifier = Modifier
                                                .sFontSize(18)
                                                .sLineHeight(23)
                                                .color(KobwebColor.hex("BED6F5"))
                                                .styleModifier { property("white-space", "nowrap") }
                                        )
                                    }
                                }
                            }
                        }
                    }
                    // Contact actions — Figma order: call, instagram, website,
                    // google, google-maps; each only when the club has the data.
                    val phone = club?.phone?.takeIf { it.isNotBlank() }
                    val instagram = club?.instagramUrl?.takeIf { it.isNotBlank() }
                    val website = club?.websiteUrl?.takeIf { it.isNotBlank() }
                    val googleSearch = club?.googleSearchUrl?.takeIf { it.isNotBlank() }
                    val googleMaps = club?.googleMapsUrl?.takeIf { it.isNotBlank() }
                    if (phone != null || instagram != null || website != null ||
                        googleSearch != null || googleMaps != null
                    ) {
                        Row(modifier = Modifier.sGap(32)) {
                            if (phone != null) {
                                ClubActionCircle(AppIcons.CallFilled, 28, 28) {
                                    window.open("tel:" + phone.filter { !it.isWhitespace() }, "_self")
                                }
                            }
                            if (instagram != null) {
                                ClubActionCircle(AppIcons.Instagram, 28, 28) {
                                    window.open(withHttpsPrefix(instagram), "_blank")
                                }
                            }
                            if (website != null) {
                                ClubActionCircle(AppIcons.GlobeFilled, 28, 28) {
                                    window.open(withHttpsPrefix(website), "_blank")
                                }
                            }
                            if (googleSearch != null) {
                                ClubActionCircle(AppIcons.Google, 28, 28) {
                                    window.open(withHttpsPrefix(googleSearch), "_blank")
                                }
                            }
                            if (googleMaps != null) {
                                ClubActionCircle(AppIcons.GoogleMaps, 22, 30) {
                                    window.open(withHttpsPrefix(googleMaps), "_blank")
                                }
                            }
                        }
                    }
                }
                // Sport pill — bottom right (Figma 11476:431833).
                if (club != null) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.End
                    ) {
                        Box(
                            modifier = Modifier
                                .sHeight(44)
                                .sBorderRadius(24)
                                .sPaddingLeft(24).sPaddingRight(24)
                                .backgroundColor(sportColors.primary)
                                .styleModifier {
                                    // Hairline white border per Figma 11476:431833 —
                                    // the gradient ring is invisible on the solid
                                    // sport-primary fill.
                                    property("border", "0.6px solid rgba(255, 255, 255, 0.4)")
                                    property("box-sizing", "border-box")
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                clubSportLabel(club),
                                modifier = Modifier
                                    .sFontSize(18)
                                    .fontWeight(AppTypography.Medium)
                                    .color(sportColors.onPrimary)
                                    .styleModifier { property("white-space", "nowrap") }
                            )
                        }
                    }
                }
            }

            // Right — photo with the rating chip (tap → stars dropdown) and
            // the favorite heart (device-local, mobile parity).
            ClubPhotoPane(
                club = club,
                reviews = reviews,
                isSubmittingReview = isSubmittingReview,
                onRate = onRate
            )
        }
    }
}

@Composable
private fun ClubPhotoPane(
    club: ClubDto?,
    reviews: List<ClubReviewDto>,
    isSubmittingReview: Boolean,
    onRate: (Int) -> Unit,
) {
    val isLoggedIn = Storage.get(StorageKeys.ACCESS_TOKEN) != null
    val myUserId = Storage.get(StorageKeys.USER_ID)
    // Ratings can only go up (backend upserts one review per user).
    val myRating = reviews.firstOrNull { it.userId == myUserId }
        ?.rating?.let { kotlin.math.round(it).toInt() } ?: 0
    var showRateDropdown by remember { mutableStateOf(false) }
    var favorite by remember(club?.id) {
        mutableStateOf(club?.id != null && favoriteClubIds().contains(club.id))
    }

    Box(
        modifier = Modifier
            .fillMaxHeight()
            .sBorderRadius(18)
            .styleModifier {
                property("flex", "1")
                property("min-width", "0")
                property("position", "relative")
                property("overflow", "visible")
                property(
                    "background",
                    "linear-gradient(201deg, rgba(60, 121, 241, 0.06) 49%, rgba(5, 4, 12, 0.06) 106%)"
                )
            }
    ) {
        Box(
            modifier = Modifier.fillMaxSize().sBorderRadius(18)
                .styleModifier { property("overflow", "hidden") }
        ) {
            if (!club?.mainPhotoUrl.isNullOrBlank()) {
                Image(
                    src = club!!.mainPhotoUrl!!,
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property("object-fit", "cover")
                    }
                )
            }
        }

        // Rating chip — glass badge top-left; signed-in users tap it to rate
        // via the stars dropdown right under it (mobile RateClubDropdown).
        if (club != null) {
            Box(
                modifier = Modifier.styleModifier {
                    property("position", "absolute")
                    property("top", s(12))
                    property("left", s(12))
                    property("z-index", "3")
                }
            ) {
                Row(
                    modifier = Modifier
                        .sBorderRadius(100)
                        .sPaddingLeft(14).sPaddingRight(14)
                        .sPaddingTop(4).sPaddingBottom(4)
                        .sGap(4)
                        .thenIf(isLoggedIn) {
                            Modifier.cursor(Cursor.Pointer)
                                .onClick { showRateDropdown = !showRateDropdown }
                        }
                        .styleModifier {
                            property("position", "relative")
                            property("background", "rgba(0, 0, 0, 0.5)")
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    GradientBorderRing(radiusPx = 100)
                    Icon(src = AppIcons.StarRating16, modifier = Modifier.sSize(16))
                    SpanText(
                        club.averageRating.asDynamic().toFixed(1) as String,
                        modifier = Modifier
                            .sFontSize(16)
                            .color(KobwebColor.hex("BED6F5"))
                    )
                }
                if (showRateDropdown) {
                    RateStarsDropdown(
                        initialRating = myRating,
                        isSubmitting = isSubmittingReview,
                        onRate = { stars ->
                            onRate(stars)
                            showRateDropdown = false
                        }
                    )
                }
            }
        }

        // Favorite heart — 44 navy circle top-right (device-local list).
        if (club != null) {
            Box(
                modifier = Modifier
                    .sSize(44)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        favorite = !favorite
                        setFavoriteClub(club.id, favorite)
                    }
                    .styleModifier {
                        property("position", "absolute")
                        property("top", s(20))
                        property("right", s(20))
                        property("z-index", "3")
                        property("background", "#07122C")
                    },
                contentAlignment = Alignment.Center
            ) {
                GradientBorderRing(radiusPx = 100)
                if (favorite) {
                    // Filled red heart when favorited.
                    Box(
                        modifier = Modifier.sSize(24).styleModifier {
                            property("background-color", "#FF4D67")
                            property("mask-image", "url('${AppIcons.Heart}')")
                            property("-webkit-mask-image", "url('${AppIcons.Heart}')")
                            property("mask-size", "contain")
                            property("-webkit-mask-size", "contain")
                            property("mask-repeat", "no-repeat")
                            property("-webkit-mask-repeat", "no-repeat")
                            property("mask-position", "center")
                            property("-webkit-mask-position", "center")
                        }
                    )
                } else {
                    // Outline-only heart until favorited (mobile parity).
                    Icon(src = AppIcons.HeartOutline, modifier = Modifier.sSize(24))
                }
            }
        }
    }
}

/** Stars-only rating dropdown under the rating chip — tapping a star submits
 *  immediately (mobile RateClubDropdown parity; ratings can only go up). */
@Composable
private fun RateStarsDropdown(
    initialRating: Int,
    isSubmitting: Boolean,
    onRate: (Int) -> Unit,
) {
    Row(
        modifier = Modifier
            .sBorderRadius(20)
            .sPaddingLeft(14).sPaddingRight(14)
            .sPaddingTop(10).sPaddingBottom(10)
            .sGap(8)
            .styleModifier {
                property("position", "absolute")
                property("top", "calc(100% + ${s(6)})")
                property("left", "0")
                property("z-index", "10")
                property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                property("border", "1px solid rgba(41, 52, 138, 0.4)")
                property("box-shadow", "0 ${s(10)} ${s(30)} rgba(0, 0, 0, 0.45)")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        repeat(5) { index ->
            val starIndex = index + 1
            // Stars below the previous vote render but aren't tappable.
            val allowed = starIndex >= initialRating && !isSubmitting
            Icon(
                src = AppIcons.StarRating16,
                modifier = Modifier
                    .sSize(28)
                    .thenIf(allowed) {
                        Modifier.cursor(Cursor.Pointer).onClick { it.stopPropagation(); onRate(starIndex) }
                    }
                    .styleModifier {
                        if (starIndex > initialRating) {
                            property("filter", "grayscale(1) opacity(0.35)")
                        }
                    }
            )
        }
    }
}

/** Gradient-bordered pill chip (Figma Segmented_Control recipe). */
@Composable
private fun InfoPillChip(onClick: (() -> Unit)?, content: @Composable () -> Unit) {
    Row(
        modifier = Modifier
            .sBorderRadius(34)
            .sPadding(2)
            .thenIf(onClick != null) {
                Modifier.cursor(Cursor.Pointer).onClick { onClick?.invoke() }
            }
            .styleModifier {
                property("position", "relative")
                property("background", AppGradients.InteractiveSecondary)
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        GradientBorderRing(radiusPx = 34)
        Row(
            modifier = Modifier
                .sHeight(36)
                .sPaddingLeft(12).sPaddingRight(12)
                .sGap(6),
            verticalAlignment = Alignment.CenterVertically
        ) {
            content()
        }
    }
}

@Composable
private fun OpenNowChip(status: OpenStatusInfo, workingHours: List<WorkingHoursDto>) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var expanded by remember { mutableStateOf(false) }
    Box(modifier = Modifier.styleModifier { property("position", "relative") }) {
        InfoPillChip(onClick = if (workingHours.isNotEmpty()) ({ expanded = !expanded }) else null) {
            // Mint status dot when open, muted otherwise.
            Box(
                modifier = Modifier.sSize(8).sBorderRadius(100).styleModifier {
                    property("background", if (status.isOpen) "#00E5A4" else "rgba(190, 214, 245, 0.4)")
                    property("flex-shrink", "0")
                }
            )
            val label = when {
                status.isOpen -> t.clubOpenLabel
                status.time != null -> t.clubOpensAt(status.time)
                else -> t.clubClosed
            }
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(18)
                    .sLineHeight(23)
                    .color(KobwebColor.hex("BED6F5"))
                    .styleModifier { property("white-space", "nowrap") }
            )
            if (status.isOpen && status.time != null) {
                Box(
                    modifier = Modifier.sSize(5).sBorderRadius(100).styleModifier {
                        property("background", "#BED6F5")
                        property("flex-shrink", "0")
                    }
                )
                SpanText(
                    t.clubUntilTime(status.time),
                    modifier = Modifier
                        .sFontSize(18)
                        .sLineHeight(23)
                        .color(KobwebColor.hex("BED6F5"))
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }
        // Weekly schedule dropdown — today highlighted with the sport colour.
        if (expanded && workingHours.isNotEmpty()) {
            val todayIso = currentIsoDay()
            Column(
                modifier = Modifier
                    .sBorderRadius(16)
                    .sPadding(12, 14)
                    .sGap(6)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "calc(100% + ${s(6)})")
                        property("left", "0")
                        property("z-index", "10")
                        property("min-width", s(220))
                        property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                        property("border", "1px solid rgba(41, 52, 138, 0.4)")
                        property("box-shadow", "0 ${s(10)} ${s(30)} rgba(0, 0, 0, 0.45)")
                    }
            ) {
                workingHours.sortedBy { it.dayOfWeek }.forEach { day ->
                    val isToday = day.dayOfWeek == todayIso
                    Row(
                        modifier = Modifier.fillMaxWidth().sGap(16),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            localizedWeekdayShort(day.dayOfWeek),
                            modifier = Modifier
                                .sFontSize(15)
                                .fontWeight(if (isToday) AppTypography.SemiBold else AppTypography.Regular)
                                .styleModifier {
                                    property(
                                        "color",
                                        if (isToday) sportColors.primary.toString() else "#BED6F5"
                                    )
                                }
                        )
                        SpanText(
                            if (day.isClosed) t.clubClosed
                            else "${day.openTime.take(5)} – ${day.closeTime.take(5)}",
                            modifier = Modifier
                                .sFontSize(15)
                                .color(if (day.isClosed) org.jetbrains.compose.web.css.Color("var(--arena-text-3)") else com.arena_web.ui.theme.ThemeVars.Text2)
                                .styleModifier { property("white-space", "nowrap") }
                        )
                    }
                }
            }
        }
    }
}

/** 59-circle contact action — gradient hairline border + navy wash. */
@Composable
private fun ClubActionCircle(
    iconSrc: String,
    iconW: Int,
    iconH: Int,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .sSize(59)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property("background", AppGradients.InteractiveSecondary)
                property("flex-shrink", "0")
                property("box-sizing", "border-box")
            },
        contentAlignment = Alignment.Center
    ) {
        GradientBorderRing(radiusPx = 100, borderWidthPx = 0.4)
        Icon(src = iconSrc, modifier = Modifier.sWidth(iconW).sHeight(iconH))
    }
}

// ── Bottom row: gallery grid | section panels (Figma 11476:431883) ──

/** Match_History_List panel recipe: rounded 30, layered navy gradients,
 *  hairline border, blur; header 24 Medium. */
@Composable
private fun ClubSectionPanel(
    title: String,
    /** Flex-basis weight in the bottom row (Figma: 452 for panels; the map
     *  gets a wider one per user preference). */
    flexBasis: Int = 452,
    content: @Composable () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxHeight()
            .sBorderRadius(30)
            .sPaddingTop(18).sPaddingBottom(12)
            .sPaddingLeft(16).sPaddingRight(16)
            .sGap(22)
            .styleModifier {
                property("flex", "$flexBasis 1 0px")
                property("min-width", "0")
                property(
                    "background",
                    "linear-gradient(24deg, rgba(0, 59, 174, 0.03) 27%, rgba(3, 87, 230, 0.03) 68%), " +
                        "linear-gradient(175deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 69%)"
                )
                property("backdrop-filter", "blur(11px)")
                property("box-sizing", "border-box")
            }
    ) {
        SpanText(
            title,
            modifier = Modifier
                .sFontSize(24)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(32)
                .sPaddingLeft(4)
                .color(KobwebColor.hex("BED6F5"))
        )
        content()
    }
}

/** Bottom-left gallery: one wide image on top, two below (Figma 11475:424964). */
@Composable
private fun ClubDetailGalleryGrid(gallery: List<ClubGalleryImageDto>) {
    if (gallery.isEmpty()) return
    Column(
        modifier = Modifier
            .fillMaxHeight()
            .sGap(12)
            .styleModifier {
                property("flex", "900 1 0px")
                property("min-width", "0")
            }
    ) {
        if (gallery.size == 2) {
            // Exactly two photos: one side-by-side row fills the whole section.
            GalleryPairRow(gallery[0].imageUrl, gallery[1].imageUrl)
        } else {
            GalleryImageSlot(
                imageUrl = gallery[0].imageUrl,
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("flex", "1")
                    property("min-height", "0")
                }
            )
            if (gallery.size >= 3) {
                GalleryPairRow(gallery[1].imageUrl, gallery[2].imageUrl)
            }
        }
    }
}

@Composable
private fun GalleryPairRow(leftUrl: String?, rightUrl: String?) {
    Div(attrs = {
        style {
            property("display", "flex")
            property("gap", s(12))
            property("flex", "1")
            property("min-height", "0")
            width(100.percent)
        }
    }) {
        listOf(leftUrl, rightUrl).forEach { url ->
            GalleryImageSlot(
                imageUrl = url,
                modifier = Modifier.styleModifier {
                    property("flex", "1")
                    property("min-width", "0")
                    property("height", "100%")
                }
            )
        }
    }
}

private fun withHttpsPrefix(url: String): String =
    if (url.startsWith("http://") || url.startsWith("https://")) url else "https://$url"

@Composable
private fun GalleryImageSlot(
    imageUrl: String?,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .sBorderRadius(22)
            .styleModifier {
                property("overflow", "hidden")
                property(
                    "background",
                    "linear-gradient(190deg, rgba(60, 121, 241, 0.06) 49%, rgba(5, 4, 12, 0.06) 106%)"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        if (!imageUrl.isNullOrBlank()) {
            Image(
                src = imageUrl,
                modifier = Modifier.fillMaxSize().styleModifier {
                    property("object-fit", "cover")
                }
            )
        }
    }
}

// ── Amenities ──

@Composable
private fun ClubDetailAmenities(amenities: List<String>) {
    // Only the amenities the club actually has (from the public detail endpoint).
    val clubAmenities = ALL_AMENITIES.filter { it.type in amenities }
    if (clubAmenities.isEmpty()) return
    Column(
        modifier = Modifier
            .sBorderRadius(22)
            .sGap(8)
            .styleModifier {
                property("flex", "1")
                property("min-height", "0")
                property("width", "100%")
                property("overflow-y", "auto")
                property("scrollbar-width", "thin")
                property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
            }
    ) {
        // 2-column grid
        val rows = clubAmenities.chunked(2)
        rows.forEach { row ->
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(12))
                    width(100.percent)
                }
            }) {
                row.forEach { info ->
                    ServiceChip(name = info.label, icon = info.icon)
                }
                if (row.size == 1) {
                    Box(modifier = Modifier.styleModifier {
                        property("flex", "1")
                        property("min-width", "0")
                    })
                }
            }
        }
    }
}

@Composable
private fun ServiceChip(name: String, icon: String?) {
    Box(
        modifier = Modifier
            .sHeight(60)
            .sBorderRadius(14)
            .styleModifier {
                property("flex", "1")
                property("min-width", "0")
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property("overflow", "hidden")
                property(
                    "background",
                    "linear-gradient(199deg, rgba(60, 121, 241, 0.096) 34%, rgba(5, 4, 12, 0.09) 67%)"
                )
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        Row(
            modifier = Modifier.sGap(8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (icon != null) {
                Icon(
                    src = icon,
                    modifier = Modifier.sSize(24)
                )
            }
            SpanText(
                name,
                modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
    }
}

// ── Map Section ──

@Composable
private fun ClubDetailMapSection(club: ClubDto?) {
    val lat = club?.latitude ?: 0.0
    val lng = club?.longitude ?: 0.0

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(18)
            .styleModifier {
                property("flex", "1")
                property("min-height", "0")
                property("overflow", "hidden")
                property("position", "relative")
            }
    ) {
        Div(attrs = {
            id("club-detail-map")
            style {
                width(100.percent)
                height(100.percent)
            }
        })

        LaunchedEffect(lat, lng) {
            delay(200)
            window.asDynamic().__clubDetailMapLat = lat
            window.asDynamic().__clubDetailMapLng = lng

            js("""
                (function() {
                    var id = 'club-detail-map';
                    var lat = window.__clubDetailMapLat;
                    var lng = window.__clubDetailMapLng;
                    var container = document.getElementById(id);
                    if (!container) return;
                    if (typeof L === 'undefined') return;

                    if (window.__clubDetailMap) {
                        try { window.__clubDetailMap.remove(); } catch(e) {}
                        window.__clubDetailMap = null;
                    }

                    container.addEventListener('wheel', function(e) { e.stopPropagation(); }, { passive: false });
                    container.addEventListener('touchmove', function(e) { e.stopPropagation(); }, { passive: false });

                    var map = L.map(id, {
                        zoomControl: false,
                        attributionControl: false,
                        scrollWheelZoom: false,
                        dragging: false,
                        doubleClickZoom: false,
                        touchZoom: false,
                        boxZoom: false
                    }).setView([lat, lng], 15);

                    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                        maxZoom: 20,
                        subdomains: 'abcd'
                    }).addTo(map);

                    var markerIcon = L.divIcon({
                        className: '',
                        html: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="26" height="35">' +
                            '<path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z" fill="#0061FF"/>' +
                            '<circle cx="12" cy="11" r="5" fill="white"/>' +
                            '</svg>',
                        iconSize: [26, 35],
                        iconAnchor: [13, 35]
                    });

                    L.marker([lat, lng], { icon: markerIcon }).addTo(map);

                    window.__clubDetailMap = map;
                    setTimeout(function() { map.invalidateSize(); }, 150);
                })();
            """)
        }

        DisposableEffect(Unit) {
            onDispose {
                js("""
                    (function() {
                        if (window.__clubDetailMap) {
                            try { window.__clubDetailMap.remove(); } catch(e) {}
                            window.__clubDetailMap = null;
                        }
                    })();
                """)
            }
        }

    }
}
